import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { TABLES, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import SchoolYearBadge from '../components/SchoolYearBadge';
import { computeStatistics, loadStatisticsData } from '../lib/statistics';

export default function StatisticsClasses() {
  const navigate = useNavigate();
  const [yearOptions, setYearOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selectedYear || selectedYear === 'all') return undefined;
    let mounted = true;
    setLoading(true);
    loadStatisticsData(selectedYear)
      .then((result) => { if (mounted) setData(result); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [selectedYear]);

  const stats = useMemo(() => (data ? computeStatistics(data) : null), [data]);

  if (!selectedYear || loading || !stats) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Statistici clase</Typography>
          <Typography variant="body2" color="text.secondary">Agregat pe clasă și comparație între clase</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
          <SchoolYearBadge year={selectedYear} />
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Medie credite per clasă
          </Typography>
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={stats.byClass}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="clasa" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="medie" fill="#4F46E5" name="Medie" />
                <Bar dataKey="max" fill="#000000" name="Max" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {stats.byClass.map((row) => (
          <Card key={row.clasa} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">Clasa {row.clasa}</Typography>
                <Chip size="small" label={`${row.elevi} elevi`} />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                <Typography variant="body2">Medie: <strong>{row.medie.toFixed(1)}</strong></Typography>
                <Typography variant="body2">Mediană: <strong>{row.median.toFixed(1)}</strong></Typography>
                <Typography variant="body2">Min: <strong>{row.min}</strong></Typography>
                <Typography variant="body2">Max: <strong>{row.max}</strong></Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {row.zero > 0 && <Chip size="small" color="error" label={`${row.zero} elevi cu 0 credite`} />}
                {row.peste80 > 0 && <Chip size="small" color="success" label={`${row.peste80} peste 80% din țintă`} />}
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/statistici/elevi?clasa=${encodeURIComponent(row.clasa)}`)}
              >
                Vezi elevi
              </Button>
            </CardContent>
          </Card>
        ))}
        {stats.byClass.length === 0 && (
          <Typography color="text.secondary">Nu există clase pentru anul selectat.</Typography>
        )}
      </Box>
    </Container>
  );
}
